// import React from "react";
import * as XLSX from "xlsx";
import { isValid } from "date-fns"
import { SidebarRoutes } from "../components/SideBar/admin.routes";
import { FORM_VALIDATIONS } from "../config/Messages.config";
import { getUserRoleAndScreens } from "../services/authServices/auth.services";
import dayjs from "dayjs";
import { ROLES } from "../config/Roles.config";

// function which returns first name of logged in user
export const extractFirstName = (email) => {
  const atIndex = email.indexOf("@");
  if (atIndex !== -1) {
    const firstPart = email.substring(0, atIndex);
    const firstName = firstPart.split(".")[0];
    return firstName;
  }
  // If "@" is not found, return the entire email address
  return email;
};


// Function to export Data Table
export const exportToExcel = (Data, Columns, FileName) => {
  // Creation of new sheet
  const wb = XLSX.utils.book_new()

  // Extracting columns of data table
  const column_data = [...Columns]

  // Extract column names from the columns array
  const columnNames = column_data.map(column => column.excelName)

  // Extract data from the data array
  // let dataRows = Data.map(row =>
  //   column_data.map(column => row[column.id])
  // )
  let dataRows = Data.map(row =>
    column_data.map(column => {
      // Check if the column is 'date' and convert to Date object
      if (column && column.isDate) {
        // Parse the date string into a Date object
        const date = new Date(row[column.excelDataField]); // Convert to a Date object
        if (!isNaN(date)) {
          return date; // Return the Date object directly
        }
      }
      return row[column.excelDataField]; // Return other fields as-is
    })
  );

  // Create a new array that includes the column names as the first row
  const wsData = [columnNames, ...dataRows]
  const ws = XLSX.utils.aoa_to_sheet(wsData)

  // Define column widths for header and data cells (set to 15 for example)
  const columnWidth = 20
  // Apply column widths to all columns (header and data)
  ws["!cols"] = Array(columnNames.length).fill({ width: columnWidth })

  XLSX.utils.book_append_sheet(wb, ws, "Data Sheet")
  const file_name = new Date().toString().split(" ")
  XLSX.writeFile(wb, `${FileName}_${file_name[1]}_${new Date().getTime()}.xlsx`)
}

// Function to capitalize first letter of each word
export const capitalizeFirstLetterOfEachWord = (str) => {
  return str.replace(/\b\w/g, char => char.toUpperCase());
}


// Function used to capitailize a text
export const replaceTextToCaplitalize = (inputValue) => {
  return inputValue.charAt(0).toUpperCase() + inputValue.slice(1);    
};

// Regular expression to check if the name is valid
const isValidHolidayName = (name) => {
  // Check if name is a non-empty string that contains at least one alphanumeric character
  return typeof name === 'string' && /^[a-zA-Z0-9 ]+$/.test(name.trim()) && name.trim().length > 0;
};

// Function to validate date format using regular expressions
const isValidDateFormat = (dateStr) => {
  const datePatterns = [
    /^\w{3} \d{2} \d{4}$/, // Aug 12 2024
    /^\d{2} \w{3} \d{4}$/, // 12 Aug 2024
    /^\d{2}\/\d{2}\/\d{4}$/, // 08/12/2024
    /^\d{2}\/\d{2}\/\d{4}$/, // 12/08/2024
    /^\d{2}-\d{2}-\d{4}$/, // 08-12-2024
    /^\d{2}-\d{2}-\d{4}$/, // 12-08-2024
    /^\d{4}-\d{2}-\d{2}$/, // 2024-08-12
    /^\d{4}\/\d{2}\/\d{2}$/, // 2024/08/12
    /^\d{2}\.\d{2}\.\d{4}$/, // 12.08.2024
    /^\d{2}\.\d{2}\.\d{4}$/, // 08.12.2024
    /^\w{3} \d{2}, \d{4}$/, // Aug 12, 2024
    /^\d{2} \w{3}, \d{4}$/, // 12 Aug, 2024
  ];
  
  return datePatterns.some((pattern) => pattern.test(dateStr));
};

// Function to convert data of a excel file into required JSON format for Holiday Master
export const convertExcelToJson = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils
        .sheet_to_json(worksheet, { header: 1 })
        .filter((row) => row.length > 0);
      const processedData = [];

      // Assuming the first row contains headers
      const [header, ...rows] = json;

      // Error handling for empty data/header or invalid column name
      if (!json[0] || !rows[0]) {
        return reject(new Error("Please upload a Excel file with valid data."));
      } else if (
        header[0] !== "Holiday Name" ||
        (header[1] !== "Date (DD-MM-YYYY)" && header[1] !== "Date")
      ) {
        return reject(
          new Error(
            "Invalid column name, Please refer to the standard excel format for reference."
          )
        );
      }

      rows.forEach((row, index) => {
        const name = row[0];
        const dateStr = row[1];
        let parsedDate = null;
        // console.log("dateStr: ", dateStr);
        // Check if dateStr is a number (Excel serial date)
        if (typeof dateStr === "number" && dateStr.toString().length > 4) {
          // Convert Excel serial date to JavaScript Date
          parsedDate = new Date((dateStr - 25569) * 86400 * 1000);
        } else if (typeof dateStr === "string" && isValidDateFormat(dateStr)) {
          // Parse the string date format
          parsedDate = new Date(Date.parse(dateStr.replace(/-/g, "/")));
        }

        if (!parsedDate || !isValid(parsedDate) || isNaN(parsedDate)) {
          console.log("isValid: ", isValidDateFormat(dateStr));
          return reject(
            new Error(
              `Invalid date format at row no. ${
                index + 2
              } Please refer to the standard excel format for reference.`
            )
          );
        }

        // Validate holiday name
        if (!isValidHolidayName(name)) {
          return reject(
            new Error(`Invalid holiday name format at row no. ${index + 2}`)
          ); // Invalid holiday name
        }

        const isoDate = parsedDate.toISOString();

        // Construct the valid JSON object
        processedData.push({
          date: isoDate,
          name: name.trim(),
        });
      });
      resolve(processedData);
    };

    reader.onerror = (e) => {
      reject(new Error('Failed to read file.'));
    };

    reader.readAsArrayBuffer(file);
  });
};
// Regular expression to check if the name is valid
const isValidActivityName = (name) => {
  // eslint-disable-next-line no-useless-escape
  const regex = /^(?![0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?`~]).*$/
  // Check if name is a non-empty string that contains at least one alphanumeric character
  return typeof name === 'string' && regex.test(name.trim()) && name.trim().length > 0;
};

// Function to convert data of a excel file into required JSON format for Check List Master
export const convertActivityNameExcelToJson = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils
        .sheet_to_json(worksheet, { header: 1 })
        .filter((row) => row.length > 0);
      const processedData = [];

      // Assuming the first row contains headers
      const [header, ...rows] = json;

      // Error handling for empty data/header or invalid column name
      if (!json[0] || !rows[0]) {
        return reject(new Error("Please upload a Excel file with valid data."));
      } else if (header[0] !== "Activity") {
        return reject(
          new Error(
            "Invalid column name, Please refer to the standard excel format for reference."
          )
        );
      }
      
      if (rows && rows[0]) {
        rows.forEach((row, index) => {
          const name = row[0];
            
          // Validate check-list point
          if (name === "" || !isValidActivityName(name)) {
            return reject(new Error(`Invalid activity name format at row no. ${index + 2}`)); // Invalid ACtivity Name
          }
  
          // Construct the valid JSON object
          processedData.push(replaceTextToCaplitalize(name.trim()));
        });
        resolve(processedData);
      }
    };

    reader.onerror = (e) => {
      reject(new Error('Failed to read file.'));
    };

    reader.readAsArrayBuffer(file);
  });
};

// Handle input text validation
export const handleValidationOnInput = e => {
  ;["e", "E", "+", "-", ".", " "].includes(e.key) && e.preventDefault()
}


// Get Page Title Name (Current path/route)
export const getPageTitle = () => {
  const { role } = getUserRoleAndScreens();
  const activePath = window.location.pathname;
  const pageTitle =
    SidebarRoutes && SidebarRoutes[0]
      ? SidebarRoutes.filter(
          (route) => route.roles.includes(role) && route.path === activePath
        )
      : [];

  if (pageTitle && pageTitle[0]) {
    return pageTitle[0].title;
  } else {
    return false;
  }
};

// extract file name from firebase storage image url
export const getFileNameFromInput = (input) => {
  // If it's a File object (blob or any File)
  if (typeof input !== "string") {
    const { name } = input;
    return name;
  }

  // If it's a Firebase URL
  if (typeof input === "string") {
    try {
      const decodedURL = decodeURIComponent(input);
      const start = decodedURL.lastIndexOf("/") + 1;
      const end = decodedURL.indexOf("?") !== -1 ? decodedURL.indexOf("?") : decodedURL.length;
      const fileName = decodedURL.substring(start, end);
      return fileName;
    } catch (error) {
      console.error("Error extracting file name:", error);
      return "";
    }
  }

  return "";
};

// Function used to check the duplicate entity in a data table
export const checkDuplicateEntity = (existingData, entity) => {
  if (existingData && existingData.includes(entity.trim().toLowerCase())) {
    return true;
  } else {
    return false;
  }
}

// Function to generate random Id
export const generateRandomId = (length = 15) => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let randomId = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    randomId += characters[randomIndex];
  }
  return randomId;
};

// Function to get the size of uploaded file in MB (.png, ,jpeg, .docx, .xlsx etc)
export const getFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
}


// Custom password validation function
const password_validation = FORM_VALIDATIONS.PASSWORD.VALIDATION
export const validateNewPassword = (value) => {
  for (let { regex, message } of password_validation) {
    if (!regex.test(value)) {
      return message;
    }
  }
  return true; // If all checks pass, return true
};

// Custom email validation function
const email_validation = FORM_VALIDATIONS.EMAIL.VALIDATIONS
export const validateEmailField = (value) => {
  for (let { regex, message } of email_validation) {
    if (!regex.test(value)) {
      return message;
    }
  }
  return true; // If all checks pass, return true
};

// Function to get country code with + sign
export const getPlusSignInCountryCode = (CountryCode) => {
  if (CountryCode && CountryCode.toString().includes("+")) {
    return CountryCode.toString();
  } else if (CountryCode && !CountryCode.toString().includes("+")) {
    return `+${CountryCode}`
  } else {
    return "";
  }
}

// Function used to format the given date in selected time zone
// export function formatDateAccordingToTimeZone(
//   inputDate,
//   returnDayjsObject = false,
//   PreferedTimeZone
// ) {
//   // Auth Date
//   // const AUTH_DATA = JSON.parse(
//   //   localStorage.getItem(process.env.REACT_APP_DEV_AUTH_USER)
//   // );
//   // const userDateFormat = AUTH_DATA?.dateFormat || "DD/MM/YYYY";
//   // const userTimeZone = AUTH_DATA?.timeZone || "Asia/Kolkata";
//   // // Handle Firebase Timestamp (in seconds or milliseconds) or ISO String
//   // let parsedDate;

//   // if (typeof utcDate === "number") {
//   //   // If it's a Firebase timestamp (seconds or milliseconds)
//   //   parsedDate = utcDate.toString().length === 10
//   //     ? dayjs.unix(utcDate) // Convert seconds to dayjs
//   //     : dayjs(utcDate); // Assume it's in milliseconds
//   // } else if (typeof utcDate === "string") {
//   //   // If it's an ISO string
//   //   parsedDate = dayjs.utc(utcDate); // Parse as UTC
//   // } else {
//   //   // Invalid date input
//   //   return returnDayjsObject ? dayjs.invalid() : "";
//   // }
//   // const convertedDate = parsedDate.tz(userTimeZone);
//   // // Convert parsed date to the selected time zone and format
//   // return returnDayjsObject ? convertedDate : convertedDate.format(userDateFormat);

//   // ------------------ New Implementation -------------------
//   // Auth Data
//   const AUTH_DATA = JSON.parse(
//     localStorage.getItem(process.env.REACT_APP_DEV_AUTH_USER)
//   );
//   const userDateFormat = AUTH_DATA?.dateFormat || "MMM DD YYYY";
//   const userTimeZone =
//     AUTH_DATA?.timeZone || PreferedTimeZone || "Asia/Kolkata";

//   let parsedDate;

//   if (typeof inputDate === "number") {
//     if (inputDate.toString().length === 10) {
//       // Handle timestamp in seconds
//       parsedDate = dayjs.unix(inputDate).tz(userTimeZone);
//     } else if (inputDate.toString().length === 13) {
//       // Handle timestamp in milliseconds
//       parsedDate = dayjs(inputDate).tz(userTimeZone);
//     } else {
//       // Invalid timestamp length
//       return returnDayjsObject ? dayjs.invalid() : "";
//     }
//   } else if (typeof inputDate === "string") {
//     // Handle ISO string format (e.g., "2025-01-02T00:00:00Z")
//     parsedDate = dayjs(inputDate).tz(userTimeZone);
//     if (!parsedDate.isValid()) {
//       return returnDayjsObject ? dayjs.invalid() : "";
//     }
//   } else {
//     // Invalid input type
//     return returnDayjsObject ? dayjs.invalid() : "";
//   }

//   // Ensure only the date is returned
//   const startOfDay = parsedDate.startOf("day");

//   // Return result
//   return returnDayjsObject ? startOfDay : startOfDay.format(userDateFormat);
// };

// function to calculate the difference in days between two ISO string dates: 
export const calculateDateDifference = (endDate) => {
  if (!endDate) {
    throw new Error("Date is required.");
  }

  const currentDate = dayjs().startOf("day");
  const end = dayjs(endDate);

  const difference = end.diff(currentDate, "day");
  return difference < 0 ? 0 : difference;// Return 0 if the endDate is in the past
};

export const addCurrentTimeInTimeStamp = (timestamp) => {
  const now = new Date();
  const date = new Date(timestamp);
  date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
  return date.toISOString();
};

// Common function to sort data according to requirement
export const sortData = (data, entity, sortType = "asc") => {
  if (!Array.isArray(data)) {
    throw new Error("The data parameter must be an array.");
  }

  return [...data].sort((a, b) => {
    if (a[entity] === undefined || b[entity] === undefined) {
      throw new Error(`The entity "${entity}" is not present in the data.`);
    }

    const comparison = a[entity].toString().localeCompare(b[entity].toString(), undefined, {
      numeric: true,
      sensitivity: "base",
    });

    return sortType === "asc" ? comparison : -comparison;
  });
};

// Function to get UTC-Offset of timezone
// export const getGMTOffset = (timezoneName) => {
//   const offsetMinutes = dayjs().tz(timezoneName).utcOffset();
//   const hours = Math.floor(Math.abs(offsetMinutes) / 60);
//   const minutes = Math.abs(offsetMinutes) % 60;
//   const sign = offsetMinutes >= 0 ? " +" : " -";
//   return `GMT${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(
//     2,
//     "0"
//   )}`;
// };

// Set the required data into dropdown/select field
export const setRequiredDataForDropdown = (setter, data, labelKey, valueKey, additionalFields = []) => {
  if (!Array.isArray(data) || data.length === 0) {
    setter([]); // Set empty array if data is missing or invalid
    return;
  }

  const formattedData = data.map(item => {
    let formattedItem = {
      label: item[labelKey] || "",
      value: item[valueKey] || "",
    };

    // Add additional fields if provided
    if (Array.isArray(additionalFields) && additionalFields?.length > 0) {
      additionalFields.forEach(field => {
        if (item.hasOwnProperty(field)) {
          formattedItem[field] = item[field];
        }
      });
    }

    return formattedItem;
  });

  setter(formattedData);
};


// Get logged-in user role: Auditee, Auditor, or Viewer
export const getLoggedInUserIsAuditeeOrAuditor = (UserType, ID) => {
  const { userUid } = getUserRoleAndScreens();
  const CASE_1 = UserType === ROLES.AUDITEE && userUid === ID;
  const CASE_2 = UserType === ROLES.AUDITOR && userUid === ID;

  if (CASE_1) {
    return {
      isAuditee: true,
    }
  } else if (CASE_2) {
    return {
      isAuditor: true,
    }
  } else {
    return { 
      isViewer: true 
    };
  }
}

// Function to display user role in audit master
export const getLoggedInUserAuditRole = (auditorId, auditeeId) => {
  const { userUid } = getUserRoleAndScreens();

  if (userUid === auditeeId) {
    return "Auditee";
  }
  if (userUid === auditorId) {
    return "Auditor";
  }
  return "Viewer"; // Default role if not Auditee or Auditor
};

// Function to display user role in audit master
export const getShortFormOfAuditRole = (roleName) => {
  if (roleName === "Auditee") {
    return "AT";
  }
  if (roleName === "Auditor") {
    return "AD";
  }
  return "V"; // Default role if not Auditee or Auditor
};

// Function to sort data alphabetically
export const sortDataAlphabetically = (data, key) => {
  if (!Array.isArray(data) || data.length === 0) return []; // Return empty array if no data
  return [...data].sort((a, b) => (a[key]?.localeCompare(b[key]) || 0));
};

// Get count of closed or open NC's of activities of perticular audit
export const countNCsOfAuditActivities = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    return { openNCs: 0, closedNCs: 0, totalNCs: 0 };
  }

  let openNCs = 0;
  let closedNCs = 0;

  data.forEach(item => {
    if (item.verificationStatus === "Open") {
      openNCs++;
    } else if (["Closed"].includes(item.verificationStatus)) {
      closedNCs++;
    }
  });

  return { openNCs, closedNCs, totalNCs: openNCs + closedNCs };
};


// Function to get data if "All" is selected for branch & dept
export const getDataForAllOption = (type, id, inputData) => {
  if (id === "All") {
    if (type === "branch") {
      const data =
        inputData && inputData[0]
          ? inputData
              .filter((branch) => branch.value !== "All")
              .map((branch) => branch.value)
          : [];
      return data;
    } else if (type === "department") {
      const data =
        inputData && inputData[0]
          ? inputData
              .filter((dept) => dept.value !== "All")
              .map((dept) => dept.value)
          : [];
      return data;
    }
  } else {
    return [id];
  }
};